/* eslint-disable camelcase */
/* eslint-disable react/no-array-index-key */
import React, { useMemo, useState } from "react";
import { Badge, Button, createStyles, Table } from "@mantine/core";
import { HandCoins, Smartphone } from "lucide-react";
import { useCompanyLimits } from "../providers/CompanyLimitsProvider";
import ConfirmUpdateCompanyLimitModal from "./Modals/ConfirmUpdateCompanyLimitModal";
import { useEnabledFeatureFlag } from "../../../common/feature-flagging/FeatureFlagProvider";
import FeatureFlags from "../../../common/feature-flagging/FeatureFlags";

interface TypeDisplayConfig {
  icon: React.ReactNode;
  label: string;
}

const TYPE_DISPLAY_MAP: Record<string, TypeDisplayConfig> = {
  ewa: {
    icon: <HandCoins size={26} color="var(--primary-primary-base)" />,
    label: "Earned Wage Access",
  },
  value_added_service: {
    icon: <Smartphone size={26} color="var(--primary-primary-base)" />,
    label: "Value Added Services",
  },
};

const getTypeDisplay = (type: string): TypeDisplayConfig =>
  TYPE_DISPLAY_MAP[type] || {
    icon: null,
    label: type,
  };

const useStyles = createStyles(() => ({
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0px",
  },
  col1: {
    width: "auto",
    padding: "0px !important",
  },
  typeCol: {
    width: "1%",
    whiteSpace: "nowrap",
    padding: "0px !important",
  },
  col2to4: {
    minWidth: "50px",
    wordWrap: "break-word",
    whiteSpace: "normal",
    padding: "0",
  },
  thtd: {
    borderBottom: "none !important",
  },
}));

interface LimitRowProps {
  title: string;
  description: string;
  limit: any;
  cycle: string;
  limitKey: string;
  modal: {
    title: string;
    description: string;
    actionTitle: string;
    actionDescription: string;
    actionCycle: string;
  };
  onUpdateLimit: (
    newValue: number,
    newState: boolean,
    intent: "enable" | "disable" | "edit"
  ) => void;
  type: string;
}

const LimitRow: React.FC<LimitRowProps> = ({
  title,
  description,
  limit,
  cycle,
  modal,
  limitKey,
  onUpdateLimit,
  type,
}) => {
  const { classes } = useStyles();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [action, setAction] = useState<"Edit" | "Disable">("Edit");

  const handleAction = (newAction: "Edit" | "Disable"): void => {
    setAction(newAction);
    setIsModalOpen(true);
  };

  const handleConfirm = (
    newValue: number,
    newState: boolean,
    intent: "enable" | "disable" | "edit"
  ): void => {
    onUpdateLimit(newValue, newState, intent);
    setIsModalOpen(false);
  };

  return (
    <tr>
      <td className={classes.typeCol}>
        <div className="flex items-center gap-2 pr-10">
          {getTypeDisplay(type).icon}
          <span className="text-jem-primary-base font-[600] text-xs">
            {getTypeDisplay(type).label}
          </span>
        </div>
      </td>
      <td className={classes.col1}>
        <div className="flex flex-col gap-1 py-4">
          <span className="text-base font-bold text-jem-primary-base">
            {title}
          </span>
          <span className="text-xs font-normal text-jem-primary-50t">
            {description}
          </span>
        </div>
      </td>
      <td className={classes.col2to4}>
        {limit?.enabled ? (
          <div className="flex flex-col gap-1">
            <span className="text-base font-normal text-jem-primary-base">
              {limitKey === "amount_limit" && "R"}
              {limit?.value} {limitKey === "request_limit" && "transactions"}
            </span>
            <span className="text-xs font-normal text-jem-primary-50t">
              {cycle}
            </span>
          </div>
        ) : (
          "-"
        )}
      </td>
      <td className={classes.col2to4}>
        <Badge
          size="sm"
          classNames={{
            root: `${
              limit?.enabled ? "bg-jem-green-10t" : "bg-jem-background_shade"
            } capitalize h-6`,
            inner: `${
              limit?.enabled ? "text-jem-green-30s" : "text-jem-primary-80t"
            }`,
          }}
        >
          {limit?.enabled ? "Enabled" : "Disabled"}
        </Badge>
      </td>
      <td className={classes.col2to4}>
        <div className="flex justify-end w-full gap-4">
          {limit?.enabled ? (
            <>
              <Button
                size="xs"
                variant="outline"
                onClick={() => handleAction("Edit")}
              >
                Edit
              </Button>
              <Button
                size="xs"
                variant="outline"
                onClick={() => handleAction("Disable")}
              >
                Disable
              </Button>
            </>
          ) : (
            <Button
              size="xs"
              variant="outline"
              onClick={() => handleAction("Edit")}
            >
              Enable
            </Button>
          )}
        </div>
      </td>
      <ConfirmUpdateCompanyLimitModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        onConfirm={handleConfirm}
        action={action}
        limit={limit}
        modal={modal}
        limitKey={limitKey}
      />
    </tr>
  );
};

const CompanyLimitsTable: React.FC = () => {
  const { classes } = useStyles();
  const { companyLimits, updateCompanyLimits } = useCompanyLimits();
  const ewa_volume_limits_feature = useEnabledFeatureFlag(
    FeatureFlags.EWA_VOLUME_LIMITS
  );

  const { standard, instant, volume_and_value, value_added_service } =
    companyLimits || {};

  const amountLimits = [
    {
      title: "Total per pay cycle",
      description: "Maximum total amount that can be withdrawn in a pay cycle.",
      limit: volume_and_value?.amount_limit,
      limitType: "volume_and_value" as const,
      limitKey: "amount_limit" as const,
      cycle: "per pay cycle",
      modal: {
        title: "Total amount per pay cycle",
        description:
          "The maximum total amount an employee can withdraw in a pay cycle.",
        actionTitle: "Set limit amount",
        actionDescription: "Employees may transact a maximum of:",
        actionCycle: "per pay cycle",
      },
      handleUpdate: (
        newValue: number,
        enabled: boolean,
        intent: "enable" | "disable" | "edit"
      ) => {
        updateCompanyLimits(
          "volume_and_value",
          "amount_limit",
          newValue,
          enabled,
          intent
        );
      },
      enabled: ewa_volume_limits_feature,
      type: "ewa",
    },
    {
      title: "Per standard transaction",
      description:
        "Maximum amount that can be withdrawn per standard transaction.",
      limit: standard?.amount_limit,
      limitType: "standard" as const,
      limitKey: "amount_limit" as const,
      cycle: "per transaction",
      modal: {
        title: "Amount per standard transaction",
        description:
          "The maximum total amount an employee can withdraw per standard transaction.",
        actionTitle: "Set limit amount",
        actionDescription: "Employees may transact a maximum of:",
        actionCycle: "per standard transaction",
      },
      handleUpdate: (
        newValue: number,
        enabled: boolean,
        intent: "enable" | "disable" | "edit"
      ) => {
        updateCompanyLimits(
          "standard",
          "amount_limit",
          newValue,
          enabled,
          intent
        );
      },
      enabled: true,
      type: "ewa",
    },
    {
      title: "Per instant transaction",
      description:
        "Maximum amount that can be withdrawn per instant transaction.",
      limit: instant?.amount_limit,
      limitType: "instant" as const,
      limitKey: "amount_limit" as const,
      cycle: "per transaction",
      modal: {
        title: "Amount per instant transaction",
        description:
          "The maximum total amount an employee can withdraw per instant transaction.",
        actionTitle: "Set limit amount",
        actionDescription: "Employees may transact a maximum of:",
        actionCycle: "per instant transaction",
      },
      handleUpdate: (
        newValue: number,
        enabled: boolean,
        intent: "enable" | "disable" | "edit"
      ) => {
        updateCompanyLimits(
          "instant",
          "amount_limit",
          newValue,
          enabled,
          intent
        );
      },
      enabled: true,
      type: "ewa",
    },
    {
      title: "Total amount per pay cycle",
      description: "Maximum amount that can be withdrawn per pay cycle.",
      limit: value_added_service?.amount_limit,
      limitType: "value_added_service" as const,
      limitKey: "amount_limit" as const,
      cycle: "per pay cycle",
      modal: {
        title: "Total amount per pay cycle",
        description:
          "The maximum total amount an employee can withdraw per pay cycle.",
        actionTitle: "Set limit amount",
        actionDescription: "Employees may transact a maximum of:",
        actionCycle: "per pay cycle",
      },
      handleUpdate: (
        newValue: number,
        enabled: boolean,
        intent: "enable" | "disable" | "edit"
      ) => {
        updateCompanyLimits(
          "value_added_service",
          "amount_limit",
          newValue,
          enabled,
          intent
        );
      },
      enabled: true,
      type: "value_added_service",
    },
  ];

  const transactionLimits = [
    {
      title: "Total per pay cycle",
      description:
        "Maximum total number of advances that can be taken in a pay cycle.",
      limit: volume_and_value?.request_limit,
      limitType: "volume_and_value" as const,
      limitKey: "request_limit" as const,
      cycle: "per pay cycle",
      modal: {
        title: "Total number of transactions per pay cycle",
        description:
          "The maximum total number of transactions an employee can take in a pay cycle.",
        actionTitle: "Set limit amount",
        actionDescription: "Employees may transact a maximum of:",
        actionCycle: "transactions per pay cycle.",
      },
      handleUpdate: (
        newValue: number,
        enabled: boolean,
        intent: "enable" | "disable" | "edit"
      ) => {
        updateCompanyLimits(
          "volume_and_value",
          "request_limit",
          newValue,
          enabled,
          intent
        );
      },
      enabled: ewa_volume_limits_feature,
      type: "ewa",
    },
    {
      title: "Standard transactions",
      description: "Maximum number of standard transactions per pay cycle.",
      limit: standard?.request_limit,
      limitType: "standard" as const,
      limitKey: "request_limit" as const,
      cycle: "per pay cycle",
      modal: {
        title: "Number of standard transactions per pay cycle",
        description:
          "The maximum number of standard transactions an employee can take in a pay cycle.",
        actionTitle: "Set limit amount",
        actionDescription: "Employees may transact a maximum of:",
        actionCycle: "standard transactions per pay cycle.",
      },
      handleUpdate: (
        newValue: number,
        enabled: boolean,
        intent: "enable" | "disable" | "edit"
      ) => {
        updateCompanyLimits(
          "standard",
          "request_limit",
          newValue,
          enabled,
          intent
        );
      },
      enabled: true,
      type: "ewa",
    },
    {
      title: "Instant transactions",
      description: "Maximum number of instant transactions per pay cycle.",
      limit: instant?.request_limit,
      limitType: "instant" as const,
      limitKey: "request_limit" as const,
      cycle: "per pay cycle",
      modal: {
        title: "Number of instant transactions per pay cycle",
        description:
          "The maximum number of instant transactions an employee can take in a pay cycle.",
        actionTitle: "Set limit amount",
        actionDescription: "Employees may transact a maximum of:",
        actionCycle: "instant transactions per pay cycle.",
      },
      handleUpdate: (
        newValue: number,
        enabled: boolean,
        intent: "enable" | "disable" | "edit"
      ) => {
        updateCompanyLimits(
          "instant",
          "request_limit",
          newValue,
          enabled,
          intent
        );
      },
      enabled: true,
      type: "ewa",
    },
    {
      title: "Total transactions per pay cycle",
      description: "Maximum number of transactions per pay cycle.",
      limit: value_added_service?.request_limit,
      limitType: "value_added_service" as const,
      limitKey: "request_limit" as const,
      cycle: "per pay cycle",
      modal: {
        title: "Total transactions per pay cycle",
        description: "Maximum number of transactions per pay cycle.",
        actionTitle: "Set limit amount",
        actionDescription: "Employees may transact a maximum of:",
        actionCycle: "transactions per pay cycle.",
      },
      handleUpdate: (
        newValue: number,
        enabled: boolean,
        intent: "enable" | "disable" | "edit"
      ) => {
        updateCompanyLimits(
          "value_added_service",
          "request_limit",
          newValue,
          enabled,
          intent
        );
      },
      enabled: true,
      type: "value_added_service",
    },
  ];

  const enabledAmountLimits = useMemo(
    () => amountLimits.filter((item) => item.limit?.enabled).length,
    [amountLimits]
  );

  const enabledTransactionLimits = useMemo(
    () => transactionLimits.filter((item) => item.limit?.enabled).length,
    [transactionLimits]
  );

  return (
    <Table className={classes.table} verticalSpacing={16}>
      <thead>
        <tr>
          <th className="!pt-8 !pb-0 !px-0 !border-b-0" colSpan={3}>
            <div className="flex items-center gap-4">
              <span className="text-lg text-jem-primary-base">
                Limits on amounts
              </span>
            </div>
          </th>
          <th
            className={`${classes.col2to4} !pt-6 !pb-0 !border-b-0 text-right`}
          >
            {enabledAmountLimits}/{amountLimits.length} enabled
          </th>
        </tr>
      </thead>
      <tbody>
        {amountLimits
          .filter((item) => item.enabled)
          .map((limitItem, idx) => (
            //  eslint-disable-next-line react/no-array-index-key
            <LimitRow
              key={idx}
              {...limitItem}
              onUpdateLimit={limitItem.handleUpdate}
            />
          ))}
      </tbody>
      <thead>
        <tr>
          <th className="!pt-8 !pb-0 !px-0 !border-b-0" colSpan={3}>
            <div className="flex items-center gap-4">
              <span className="text-lg text-jem-primary-base">
                Limits on number of transactions
              </span>
            </div>
          </th>
          <th
            className={`${classes.col2to4} !pt-6 !pb-0 !border-b-0 text-right`}
          >
            {enabledTransactionLimits}/{transactionLimits.length} enabled
          </th>
        </tr>
      </thead>
      <tbody>
        {transactionLimits
          .filter((item) => item.enabled)
          .map((limitItem, idx) => (
            //  eslint-disable-next-line react/no-array-index-key
            <LimitRow
              key={idx}
              {...limitItem}
              onUpdateLimit={limitItem.handleUpdate}
            />
          ))}
      </tbody>
    </Table>
  );
};

export default CompanyLimitsTable;
